import { useState } from 'react';
import { Layers } from 'lucide-react';
import { useRouter } from '@uirouter/react';
import { Deployment, StatefulSet } from 'kubernetes-types/apps/v1';

import { AuthorizedToNamespace } from '@/react/hooks/useUser';
import { notifySuccess, notifyError } from '@/portainer/services/notifications';
import { EnvironmentId } from '@/react/portainer/environments/types';

import { Button } from '@@/buttons';
import { Icon } from '@@/Icon';
import { Modal, ModalType, openModal, OnSubmit } from '@@/modals';
import { Input } from '@@/form-components/Input';

import { applicationIsKind } from '../../utils';
import { Application } from '../../types';
import { usePatchApplicationMutation } from '../../queries/usePatchApplicationMutation';

type Props = {
  environmentId: EnvironmentId;
  namespace: string;
  appName: string;
  app?: Application;
};

export function ScaleApplicationButton({
  environmentId,
  namespace,
  appName,
  app,
}: Props) {
  const router = useRouter();
  const patchAppMutation = usePatchApplicationMutation(
    environmentId,
    namespace,
    appName
  );

  if (
    !applicationIsKind<Deployment>('Deployment', app) &&
    !applicationIsKind<StatefulSet>('StatefulSet', app)
  ) {
    return null;
  }

  if (!app.kind) {
    return null;
  }

  const appKind: 'Deployment' | 'StatefulSet' = app.kind;
  const currentReplicas = app.spec?.replicas ?? 0;

  return (
    <AuthorizedToNamespace
      namespace={namespace}
      authorizations="K8sApplicationsP"
    >
      <Button
        type="button"
        size="small"
        color="light"
        className="!ml-0"
        onClick={() => scaleApplication()}
        data-cy="k8sAppDetail-scaleButton"
      >
        <Icon icon={Layers} className="mr-1" />
        Scale
      </Button>
    </AuthorizedToNamespace>
  );

  async function scaleApplication() {
    const replicas = await confirmScale(currentReplicas);
    if (replicas === undefined || replicas === currentReplicas) {
      return;
    }

    patchAppMutation.mutateAsync(
      {
        appKind,
        patch: [{ op: 'replace', path: '/spec/replicas', value: replicas }],
        contentType: 'application/json-patch+json',
      },
      {
        onSuccess: () => {
          notifySuccess('Success', 'Application successfully scaled');
          router.stateService.reload();
        },
        onError: (error) =>
          notifyError(
            'Failure',
            error as Error,
            'Unable to scale the application'
          ),
      }
    );
  }
}

function confirmScale(currentReplicas: number) {
  return openModal(ConfirmScaleModal, { currentReplicas });
}

function ConfirmScaleModal({
  currentReplicas,
  onSubmit,
}: {
  currentReplicas: number;
  onSubmit: OnSubmit<number>;
}) {
  const [replicas, setReplicas] = useState(currentReplicas);
  const isInvalid = Number.isNaN(replicas) || replicas < 0;

  return (
    <Modal onDismiss={() => onSubmit()} aria-label="confirm scale application">
      <Modal.Header title="Are you sure?" modalType={ModalType.Warn} />
      <Modal.Body>
        <p>
          Scaling the application will change the number of running replicas. Do
          you wish to continue?
        </p>
        <div className="form-group flex items-center">
          <label htmlFor="scale-replicas-input" className="control-label w-1/2">
            Number of replicas
          </label>
          <div className="w-1/2">
            <Input
              id="scale-replicas-input"
              type="number"
              min={0}
              step={1}
              value={replicas}
              onChange={(event) => setReplicas(event.target.valueAsNumber)}
              // disabled because it makes sense to auto focus once the modal is mounted
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              data-cy="k8sAppDetail-scaleInput"
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={() => onSubmit()}
          color="default"
          size="medium"
          data-cy="k8sAppDetail-scaleCancelButton"
        >
          Cancel
        </Button>
        <Button
          onClick={() => onSubmit(replicas)}
          color="primary"
          size="medium"
          disabled={isInvalid || replicas === currentReplicas}
          data-cy="k8sAppDetail-scaleConfirmButton"
        >
          Scale
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
